import { useState, useCallback, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";

export const useFavorites = () => {
  const { user } = useAuth();
  const [favorites, setFavorites] = useState<string[]>([]);
  const [authPromptOpen, setAuthPromptOpen] = useState(false);

  // Load favorites from DB when user logs in
  useEffect(() => {
    if (!user) {
      setFavorites([]);
      return;
    }

    const load = async () => {
      const { data } = await supabase
        .from("favorites")
        .select("property_id")
        .eq("user_id", user.id);
      setFavorites(data?.map((f) => f.property_id) || []);
    };
    load();
  }, [user]);

  const toggleFavorite = useCallback(
    async (propertyId: string) => {
      // Require auth, show prompt instead of silently saving locally
      if (!user) {
        setAuthPromptOpen(true);
        return;
      }

      const isFav = favorites.includes(propertyId);

      // Optimistic update
      setFavorites((prev) =>
        isFav ? prev.filter((id) => id !== propertyId) : [...prev, propertyId]
      );

      if (isFav) {
        await supabase
          .from("favorites")
          .delete()
          .eq("user_id", user.id)
          .eq("property_id", propertyId);
      } else {
        await supabase
          .from("favorites")
          .insert({ user_id: user.id, property_id: propertyId });
      }
    },
    [favorites, user]
  );

  const isFavorite = useCallback(
    (propertyId: string) => favorites.includes(propertyId),
    [favorites]
  );

  return { favorites, toggleFavorite, isFavorite, authPromptOpen, setAuthPromptOpen };
};
